import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, RefreshCw, LineChart as LineChartIcon, BarChart3 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';

interface FXForecastCardProps {
  baseCurrency?: string;
  companyId?: string;
}

export function FXForecastCard({ baseCurrency = 'USD', companyId }: FXForecastCardProps) {
  const [windowDays, setWindowDays] = useState(30);
  const [horizon, setHorizon] = useState(90);
  const [forecast, setForecast] = useState<any>(null);
  const [scenarios, setScenarios] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [runningScenarios, setRunningScenarios] = useState(false);
  const { toast } = useToast();

  async function loadForecast() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('fx_forecasts')
        .select('date, predicted_rates, horizon_days')
        .eq('base_currency', baseCurrency)
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setForecast(data);
    } catch (error: any) {
      toast({
        title: 'Error loading forecast',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function computeForecast() {
    setComputing(true);
    try {
      const { data, error } = await supabase.functions.invoke('fx-forecast', {
        body: { base: baseCurrency, windowDays, horizonDays: horizon },
      });

      if (error) throw error;

      toast({
        title: 'Forecast generated',
        description: `Created ${horizon}-day FX forecast using ${windowDays}-day history`,
      });

      setForecast(data);
    } catch (error: any) {
      toast({
        title: 'Error generating forecast',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setComputing(false);
    }
  }

  async function runScenarios() {
    if (!companyId) {
      toast({
        title: 'Company required',
        description: 'Select a company to run scenarios',
        variant: 'destructive',
      });
      return;
    }

    setRunningScenarios(true);
    try {
      const { data, error } = await supabase.functions.invoke('fx-scenario', {
        body: {
          companyId,
          base: baseCurrency,
          horizonDays: horizon,
          scenarios: [
            { label: 'Strong', change: +5 },
            { label: 'Neutral', change: 0 },
            { label: 'Weak', change: -5 },
          ],
        },
      });

      if (error) throw error;

      setScenarios(data.results || []);
      toast({
        title: 'Scenarios computed',
        description: 'FX impact on runway calculated',
      });
    } catch (error: any) {
      toast({
        title: 'Error running scenarios',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setRunningScenarios(false);
    }
  }

  useEffect(() => {
    loadForecast();
  }, [baseCurrency]);

  // Prepare chart data
  const chartData = forecast?.predicted_rates
    ? Object.entries(forecast.predicted_rates)
        .filter(([currency]) => ['USD', 'ZAR', 'GBP', 'EUR', 'AED', 'KES'].includes(currency))
        .map(([currency, rate]) => ({
          currency,
          rate: Number(rate),
        }))
    : [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <LineChartIcon className="h-5 w-5" />
              FX Forecast & Scenarios
            </CardTitle>
            <CardDescription>
              AI-powered exchange rate predictions and runway impact analysis
            </CardDescription>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={String(horizon)} onValueChange={(v) => setHorizon(parseInt(v))}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={computeForecast} disabled={computing}>
              {computing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Forecast
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="p-6 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : forecast ? (
          <>
            <p className="text-sm text-muted-foreground">
              Forecast horizon: {forecast.horizon_days} days | Generated:{' '}
              {new Date(forecast.date).toLocaleDateString()}
            </p>

            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="currency" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="rate"
                    stroke="hsl(var(--primary))"
                    name="Predicted Rate"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {companyId && (
              <div className="flex justify-end">
                <Button size="sm" onClick={runScenarios} disabled={runningScenarios}>
                  {runningScenarios ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <BarChart3 className="h-4 w-4 mr-2" />
                  )}
                  Run Scenarios
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <p>No forecast yet. Click "Forecast" to generate predictions.</p>
          </div>
        )}

        {scenarios.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Scenario Results (Runway Impact)</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {scenarios.map((s: any) => (
                <div key={s.label} className="border rounded-md p-3 bg-card space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{s.label}</span>
                    <span className="text-sm text-muted-foreground">
                      {s.change_percent > 0 ? '+' : ''}
                      {s.change_percent}%
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Runway: </span>
                    <span className="font-semibold">
                      {s.runway_months?.toFixed(1) || 'N/A'} months
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    FX Rate: {s.fx_rate?.toFixed(3)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
